import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { FilterOperator, FilterType, processFilterValue } from "@/lib/filters";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDataTableSettings } from "../hooks/useDataTableSettings";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, Filter, XIcon } from "lucide-react";
import { buildMultipleFilterValues } from "@/lib/filters/buildMultipleFilterValues";
import { ClearFilterIconButton } from "./ClearFilterIconButton";

interface EnumFilterProps {
  columnId: string;
  options: Readonly<string[]>;
  translationKey: string;
  placeholder?: string;
  renderOption?: (option: string, translation: string) => React.ReactNode;
  /**
   * Optional extra options mapped to an explicit filter payload.
   * Example: IS_EMPTY_ENUM_OPTION -> { filterType: FilterType.IsEmpty, filterValue: "" }.
   */
  additionalOptions?: Readonly<EnumOption[]>;
}

export interface EnumOption {
  value: string;
  label?: string;
  labelKey?: string;
  filterType?: FilterType;
  filterValue?: string;
}

export const IS_EMPTY_ENUM_OPTION: EnumOption = {
  value: "is_empty",
  labelKey: "table.filters.isEmpty",
  filterType: FilterType.IsEmpty,
  filterValue: "",
};

export const EnumFilter = ({
  options,
  translationKey,
  columnId,
  placeholder = "table.choose",
  renderOption,
  additionalOptions = [],
}: EnumFilterProps) => {
  const { t } = useTranslation();
  const { options: dataTableOptions } = useDataTableSettings();
  const isFilteredOrSorted = dataTableOptions.isFilteredOrSorted(columnId);
  const [open, setOpen] = useState(false);

  const resolvedOptions = useMemo<EnumOption[]>(
    () => [
      ...options.map((value) => ({
        value,
        label: t(`${translationKey}.${value}`),
      })),
      ...additionalOptions.map((option) => ({
        value: option.value,
        label:
          option.label ?? (option.labelKey ? t(option.labelKey) : option.value),
        filterType: option.filterType,
        filterValue: option.filterValue,
      })),
    ],
    [options, additionalOptions, t, translationKey],
  );

  const optionMap = useMemo(
    () => new Map(resolvedOptions.map((option) => [option.value, option])),
    [resolvedOptions],
  );

  const columnFilterValue = useMemo(() => {
    const filterValue = dataTableOptions.getFilterValue(columnId);
    if (!filterValue) {
      return [];
    }

    const matchingAdditionalOption = resolvedOptions.find(
      (option) =>
        option.filterType === filterValue.filter &&
        option.filterValue === (filterValue.value ?? ""),
    );
    if (matchingAdditionalOption) {
      return [matchingAdditionalOption.value];
    }

    if (!filterValue.value) {
      return [];
    }

    return processFilterValue(filterValue.value);
  }, [columnId, dataTableOptions, resolvedOptions]);

  const onClear = useCallback(() => {
    dataTableOptions.setFilteredMark(columnId, false);
    dataTableOptions.setFilterValue(columnId, {
      value: "",
      filter: FilterType.EqualsString,
    });
    setOpen(false);
  }, [columnId, dataTableOptions]);

  const onSelect = useCallback(
    (value: string) => {
      const selectedOption = optionMap.get(value);
      if (!selectedOption) {
        return;
      }

      if (selectedOption.filterType !== undefined) {
        const isAlreadySelected = columnFilterValue.includes(value);
        if (isAlreadySelected) {
          onClear();
          return;
        }

        dataTableOptions.setFilteredMark(columnId, true);
        dataTableOptions.setFilterValue(columnId, {
          value: selectedOption.filterValue ?? "",
          filter: selectedOption.filterType,
        });
        return;
      }

      const nextFilterValues = columnFilterValue.includes(value)
        ? columnFilterValue.filter((v) => v !== value)
        : [
            ...columnFilterValue.filter(
              (v) => optionMap.get(v)?.filterType === undefined,
            ),
            value,
          ];
      dataTableOptions.setFilteredMark(columnId, nextFilterValues.length > 0);
      dataTableOptions.setFilterValue(columnId, {
        value: buildMultipleFilterValues(nextFilterValues, FilterOperator.OR),
        filter: FilterType.EqualsString,
      });
    },
    [columnId, columnFilterValue, dataTableOptions, onClear, optionMap],
  );

  const displayText = useMemo(() => {
    if (!columnFilterValue.length) {
      return t(placeholder);
    }
    return columnFilterValue
      .map((value) => optionMap.get(value)?.label)
      .filter((value): value is string => Boolean(value))
      .join(", ");
  }, [columnFilterValue, optionMap, placeholder, t]);

  return (
    <div className="flex gap-1 items-center min-w-0">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full min-w-0 justify-between p-2 pl-3 text-sm h-10 hover:bg-white",
              {
                "border-blue-500/50 text-blue-700 hover:text-blue-900 hover:bg-blue-200":
                  isFilteredOrSorted,
              },
            )}
          >
            <span className="flex flex-row gap-2 items-center overflow-hidden">
              <Filter className="h-4 w-4 shrink-0" />
              <span className="truncate">{displayText}</span>
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="min-w-[200px] w-[--radix-popover-trigger-width] p-0">
          <Command>
            <CommandInput placeholder={t("common.search")} />
            <CommandList>
              <CommandEmpty>{t("common.noResults")}</CommandEmpty>
              <CommandGroup>
                {resolvedOptions.map((option) => {
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onSelect={onSelect}
                      keywords={[option.label ?? ""]}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          columnFilterValue.includes(option.value)
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                      />
                      <span className="flex-1 text-left">
                        {renderOption
                          ? renderOption(option.value, option.label ?? "")
                          : option.label}
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              {columnFilterValue.length > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem onSelect={onClear} className="gap-2">
                      <XIcon className="w-4 h-4" />
                      {t("common.clear")}
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <ClearFilterIconButton
        onClick={onClear}
        visible={columnFilterValue.length > 0}
      />
    </div>
  );
};
